


























































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import http from "@/axios/http-common";
import { debounce } from "lodash";

interface ObatLink {
  uid?: string;
  kd_obat?: string;
  nama_obat?: string;
  dosis?: string;
  rute_id?: number | null;
  rute_text?: string;
  frekuensi_id?: number | null;
  frekuensi_text?: string;
  qty?: number | null;
  satuan?: string;
  keterangan?: string;
  cpo_id?: number;
  cpo_item_id?: number;
  cpo_item_pemberian_id?: number;
  _narrative?: string;
  _k?: number;
}

@Component
export default class ImplementasiObatInline extends Vue {
  @Prop({ default: () => ({}) }) readonly value!: ObatLink;
  @Prop({ default: null }) readonly registerId!: number | null;
  @Prop({ default: 1 }) readonly indexLabel!: number;
  @Prop({ default: false }) readonly removable!: boolean;

  selectedObat: any = null;
  selectedRute: any = null;
  selectedFrekuensi: any = null;
  dosis = "";
  qty: number | null = null;
  keterangan = "";

  obatItems: any[] = [];
  ruteItems: any[] = [];
  frekuensiItems: any[] = [];

  obatSearch = "";
  ruteSearch = "";
  frekuensiSearch = "";

  loadingObat = false;
  loadingRute = false;
  loadingFrekuensi = false;

  // CPO source-of-truth picker (drugs already prescribed for this register)
  cpoRows: any[] = [];
  loadingCpo = false;
  mode: "cpo" | "baru" = "baru";
  selectedCpoRow: any = null;
  pickedCpoItemId: number | null = null;

  debouncedSearchObat = debounce(this.searchObat, 300);
  debouncedSearchRute = debounce(this.searchRute, 300);
  debouncedSearchFrekuensi = debounce(this.searchFrekuensi, 300);

  mounted() {
    this.hydrateFromValue();
    // preload rute & frekuensi lists (small lookup tables)
    this.searchRute("");
    this.searchFrekuensi("");
    this.loadCpoRows();
  }

  async loadCpoRows() {
    if (!this.registerId) {
      this.mode = "baru";
      return;
    }
    this.loadingCpo = true;
    try {
      const resp = await http.getData(
        `/register/${this.registerId}/catatan-pemberian-obat`
      );
      // this endpoint wraps the list as { status, data: [...] };
      // http.getData returns the raw axios response, so the array is resp.data.data
      const body = resp && resp.data;
      const rows = Array.isArray(body) ? body : (body && body.data) || [];
      this.cpoRows = rows.filter(function (r: any) {
        return r && r.nama;
      });
    } catch (e) {
      this.cpoRows = [];
    } finally {
      this.loadingCpo = false;
    }

    const v = this.value || {};
    if (v.cpo_item_id) {
      // editing a line already sourced from CPO — re-select its row
      this.mode = "cpo";
      const match = this.cpoRows.find((r: any) => r.cpo_item_id === v.cpo_item_id);
      if (match) {
        this.selectedCpoRow = match;
      }
    } else if (!v.kd_obat) {
      // fresh line: prefer picking from CPO when anything is prescribed
      this.mode = this.cpoRows.length ? "cpo" : "baru";
    }
  }

  cpoRowLabel(row: any): string {
    if (!row) return "";
    const dose = row.dosis || row.resep_dosis || "";
    const rute = row.rute || row.resep_rute || "";
    const extra = [dose, rute].filter(Boolean).join(" · ");
    return extra ? `${row.nama} — ${extra}` : row.nama;
  }

  onPickCpo(row: any) {
    if (!row) return;
    const kode = row.kode_obat || row.kode || row.disp_kd_obat;
    this.selectedObat = { id: kode, nama: row.nama, satuan: row.satuan };
    this.obatItems = [this.selectedObat];
    this.dosis = row.dosis || row.resep_dosis || "";

    const ruteId = row.resep_rute_id || null;
    const ruteText = row.rute || row.resep_rute || "";
    this.selectedRute = ruteText ? { id: ruteId, nama: ruteText } : null;
    if (this.selectedRute) this.ruteItems = [this.selectedRute];

    const frekId = row.resep_frekuensi_id || null;
    const frekText = row.jadwal || row.resep_frekuensi || "";
    this.selectedFrekuensi = frekText ? { id: frekId, nama: frekText } : null;
    if (this.selectedFrekuensi) this.frekuensiItems = [this.selectedFrekuensi];

    this.qty = row.total_qty != null ? Number(row.total_qty) : null;
    this.pickedCpoItemId = row.cpo_item_id != null ? row.cpo_item_id : null;
  }

  onModeChange() {
    // leaving CPO mode: drop the link so the backend dedupes/creates by drug
    if (this.mode === "baru") {
      this.selectedCpoRow = null;
      this.pickedCpoItemId = null;
    }
  }

  hydrateFromValue() {
    const v = this.value || {};
    if (v.cpo_item_id) {
      this.pickedCpoItemId = v.cpo_item_id;
    }
    if (v.kd_obat) {
      this.selectedObat = {
        id: v.kd_obat,
        nama: v.nama_obat || v.kd_obat,
        satuan: v.satuan
      };
      this.obatItems = [this.selectedObat];
    }
    if (v.rute_id) {
      this.selectedRute = { id: v.rute_id, nama: v.rute_text };
      this.ruteItems = [this.selectedRute];
    }
    if (v.frekuensi_id) {
      this.selectedFrekuensi = { id: v.frekuensi_id, nama: v.frekuensi_text };
      this.frekuensiItems = [this.selectedFrekuensi];
    }
    this.dosis = v.dosis || "";
    this.qty = v.qty ?? null;
    this.keterangan = v.keterangan || "";
  }

  @Watch("obatSearch")
  onObatSearch(val: string) {
    if (!val || val.length < 2) return;
    this.debouncedSearchObat(val);
  }

  @Watch("ruteSearch")
  onRuteSearch(val: string) {
    this.debouncedSearchRute(val || "");
  }

  @Watch("frekuensiSearch")
  onFrekuensiSearch(val: string) {
    this.debouncedSearchFrekuensi(val || "");
  }

  async searchObat(term: string) {
    this.loadingObat = true;
    try {
      const resp = await http.getData(
        `/referensi/obat/search?term=${encodeURIComponent(term)}&kategori=o`
      );
      this.obatItems = (resp.data || []).map((d: any) => ({
        id: d.id,
        nama: d.nama,
        satuan: d.satuan
      }));
    } finally {
      this.loadingObat = false;
    }
  }

  async searchRute(term: string) {
    this.loadingRute = true;
    try {
      const resp = await http.getData(
        `/referensi/resep-rute?term=${encodeURIComponent(term)}`
      );
      this.ruteItems = resp.data || [];
    } finally {
      this.loadingRute = false;
    }
  }

  async searchFrekuensi(term: string) {
    this.loadingFrekuensi = true;
    try {
      const resp = await http.getData(
        `/referensi/resep-frekuensi?term=${encodeURIComponent(term)}`
      );
      this.frekuensiItems = resp.data || [];
    } finally {
      this.loadingFrekuensi = false;
    }
  }

  get displayName(): string {
    if (!this.selectedObat) return "";
    const ruteLabel = this.selectedRute ? this.selectedRute.nama : "";
    const verb = this.guessActionVerb(ruteLabel);
    const parts = [
      verb,
      this.selectedObat.nama,
      this.dosis,
      ruteLabel ? `(${ruteLabel})` : ""
    ].filter(Boolean);
    return parts.join(" ").trim();
  }

  guessActionVerb(rute: string): string {
    const r = (rute || "").toLowerCase();
    if (r.includes("iv") || r.includes("im") || r.includes("sc"))
      return "Injeksi";
    if (r.includes("oral") || r.includes("po")) return "Pemberian Oral";
    if (r.includes("inhal") || r.includes("nebul")) return "Inhalasi";
    if (r.includes("topi") || r.includes("kulit")) return "Pemberian Topikal";
    if (r.includes("rekt") || r.includes("supp")) return "Pemberian Rektal";
    if (r.includes("subling")) return "Pemberian Sublingual";
    return "Pemberian Obat";
  }

  get serialized(): ObatLink {
    return {
      uid: this.value && this.value.uid ? this.value.uid : undefined,
      kd_obat: this.selectedObat ? this.selectedObat.id : undefined,
      nama_obat: this.selectedObat ? this.selectedObat.nama : undefined,
      satuan: this.selectedObat ? this.selectedObat.satuan : undefined,
      dosis: this.dosis || undefined,
      rute_id: this.selectedRute ? this.selectedRute.id : null,
      rute_text: this.selectedRute ? this.selectedRute.nama : undefined,
      frekuensi_id: this.selectedFrekuensi ? this.selectedFrekuensi.id : null,
      frekuensi_text: this.selectedFrekuensi
        ? this.selectedFrekuensi.nama
        : undefined,
      qty: this.qty,
      keterangan: this.keterangan || undefined,
      cpo_id: this.value && this.value.cpo_id,
      cpo_item_id:
        this.pickedCpoItemId != null
          ? this.pickedCpoItemId
          : this.value && this.value.cpo_item_id,
      cpo_item_pemberian_id: this.value && this.value.cpo_item_pemberian_id,
      _narrative: this.displayName || undefined
    };
  }

  // Emit ONLY when the user changes a local input. `serialized` reads `this.value`
  // (uid/cpo passthrough), so watching it would re-fire when the parent writes the
  // emitted payload back into `value` — an infinite loop that freezes the page.
  // Watching local state instead breaks that loop: a `value` write-back leaves these
  // fields untouched, so no re-emit occurs.
  get localState() {
    return {
      obatId: this.selectedObat ? this.selectedObat.id : null,
      namaObat: this.selectedObat ? this.selectedObat.nama : null,
      satuan: this.selectedObat ? this.selectedObat.satuan : null,
      ruteId: this.selectedRute ? this.selectedRute.id : null,
      ruteText: this.selectedRute ? this.selectedRute.nama : null,
      frekuensiId: this.selectedFrekuensi ? this.selectedFrekuensi.id : null,
      frekuensiText: this.selectedFrekuensi ? this.selectedFrekuensi.nama : null,
      dosis: this.dosis,
      qty: this.qty,
      keterangan: this.keterangan,
      pickedCpoItemId: this.pickedCpoItemId
    };
  }

  @Watch("localState", { deep: true })
  onLocalStateChanged() {
    // serialized carries `_narrative`, so the parent builds the row name from
    // the emitted payload — no separate display-name event needed
    this.$emit("input", this.serialized);
  }
}
